import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, LinkButton, Notice, TextField } from './ui.js';

const MAX_BYTES = 256 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const BRANDING_KEY = ['account', 'report-branding'] as const;

/** The logo and name used on the printable site report (T-66, Business and Agency). */
export function useReportBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => unwrap(api.GET('/account/report-branding')),
  });
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

/** Account settings: report name and logo (upload, preview, remove). */
export function ReportBrandingCard() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const branding = useReportBranding();
  const [name, setName] = useState('');
  // undefined: keep the stored logo; null: remove it; string: the new one (data: URL).
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [fileError, setFileError] = useState<string>();
  useEffect(() => {
    if (branding.data) setName(branding.data.name ?? '');
  }, [branding.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/account/report-branding', {
          body: { name: name.trim() || null, ...(logo === undefined ? {} : { logo }) },
        }),
      ),
    onSuccess: (data) => {
      client.setQueryData(BRANDING_KEY, data);
      setLogo(undefined);
    },
  });
  if (!branding.data) return null;
  const b = branding.data;
  const preview = logo === undefined ? b.logo : logo;

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TYPES.includes(file.type)) return setFileError(t('branding.wrongType'));
    if (file.size > MAX_BYTES) return setFileError(t('branding.tooLarge'));
    setFileError(undefined);
    setLogo(await readAsDataUrl(file));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <h2 className="mb-1 text-xl font-semibold">{t('branding.title')}</h2>
      <p className="mb-4 text-slate-700">{t('branding.intro')}</p>
      {!b.available ? (
        <Notice kind="info">
          {t('branding.planNeeded')}{' '}
          <LinkButton to="/billing" variant="soft">
            {t('branding.upgrade')}
          </LinkButton>
        </Notice>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <TextField
            label={t('branding.name')}
            hint={t('branding.nameHint')}
            optional
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="flex flex-col gap-2">
            <span className="font-semibold">{t('branding.logo')}</span>
            {preview ? (
              <div className="flex flex-wrap items-center gap-3">
                <img
                  src={preview}
                  alt={t('branding.logoAlt')}
                  className="max-h-16 max-w-48 rounded border border-slate-200 bg-white object-contain p-1"
                />
                <Button type="button" variant="secondary" onClick={() => setLogo(null)}>
                  {t('branding.remove')}
                </Button>
              </div>
            ) : null}
            <label className="inline-flex min-h-11 w-fit cursor-pointer items-center rounded-md border border-blue-700 px-3 font-semibold text-blue-800 hover:bg-blue-50 focus-within:outline-2 focus-within:outline-blue-700">
              {preview ? t('branding.replace') : t('branding.upload')}
              <input
                type="file"
                accept={TYPES.join(',')}
                className="sr-only"
                onChange={(e) => void pick(e)}
              />
            </label>
            <span className="text-sm text-slate-700">{t('branding.logoHint')}</span>
            {fileError ? <Notice kind="error">{fileError}</Notice> : null}
          </div>
          {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
          {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
          <div>
            <Button type="submit" busy={save.isPending}>
              {t('app.save')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
